import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  formatPeriodRange,
  fromWall,
  nextFullHour,
  normalizeOccupancySteps,
  occupancyState,
  priceSentence,
  rentalFactor,
  rentalLineTotal,
  rentalUnitCopy,
  wallParts,
} from '../../../utils/rentalPeriod'
import './RentalPeriodPicker.css'

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const WEEKDAY_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
const MONTH_NAMES = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
]
const MONTH_SHORT = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
const HOURS = Array.from({ length: 24 }, (_, index) => index)
const MINUTES = Array.from({ length: 12 }, (_, index) => index * 5)
const WHEEL_ROW_HEIGHT = 40

const pad2 = (value) => String(value).padStart(2, '0')
const STATE_LABEL = { free: 'свободно', partial: 'частично', booked: 'занято' }

function readySteps(occupancyByMonth, year, month) {
  const record = occupancyByMonth?.[`${year}-${month}`]
  return record?.status === 'ready' ? record.steps : null
}

function bucketState(steps, start, end, quantity, allowPast, now = Date.now()) {
  if (!steps) return null
  let fromMs = start.getTime()
  const toMs = end.getTime()
  if (!(toMs > fromMs)) return null
  if (!allowPast) {
    if (toMs <= now) return null
    if (fromMs < now) fromMs = now
    if (fromMs >= toMs) return null
  }
  return occupancyState(steps, fromMs, toMs, quantity)
}

function sameWallDay(a, b) {
  const first = wallParts(a)
  const second = wallParts(b)
  return first.year === second.year && first.month === second.month && first.day === second.day
}

function formatMoney(value) {
  const amount = Number(value)
  if (!Number.isFinite(amount)) return '0'
  return amount.toLocaleString('ru-RU', { maximumFractionDigits: 2 })
}

function formatRangeDate(date) {
  const parts = wallParts(date)
  const weekday = WEEKDAY_SHORT[new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()]
  return `${weekday}, ${parts.day} ${MONTH_SHORT[parts.month - 1]}`
}

function formatRangeTime(date) {
  const parts = wallParts(date)
  return `${pad2(parts.hour)}:${pad2(parts.minute)}`
}

function TimeWheel({ values, selected, onSelect, ariaLabel, stateAt }) {
  const scrollRef = useRef(null)
  const timerRef = useRef(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return undefined
    const index = Math.max(0, values.indexOf(selected))
    el.scrollTop = index * WHEEL_ROW_HEIGHT
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
    // center the current value when the wheel mounts
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleScroll() {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      const el = scrollRef.current
      if (!el) return
      const index = Math.round(el.scrollTop / WHEEL_ROW_HEIGHT)
      const clamped = Math.max(0, Math.min(values.length - 1, index))
      const value = values[clamped]
      if (value !== selected) onSelect?.(value)
    }, 90)
  }

  return (
    <div className="rental-wheel" role="listbox" aria-label={ariaLabel}>
      <div className="rental-wheel-scroll" ref={scrollRef} onScroll={handleScroll}>
        <div className="rental-wheel-pad" aria-hidden="true" />
        {values.map((value) => {
          const state = stateAt?.(value) || null
          const className = [
            'rental-wheel-option',
            value === selected ? 'rental-wheel-option--selected' : '',
            state ? `rental-wheel-option--${state}` : '',
          ].filter(Boolean).join(' ')
          return (
          <button
            key={value}
            type="button"
            role="option"
            aria-selected={value === selected}
            aria-label={state ? `${pad2(value)}, ${STATE_LABEL[state]}` : undefined}
            className={className}
            onClick={() => {
              onSelect?.(value)
              const el = scrollRef.current
              if (el) {
                el.scrollTo({ top: values.indexOf(value) * WHEEL_ROW_HEIGHT, behavior: 'smooth' })
              }
            }}
          >
            {pad2(value)}
          </button>
          )
        })}
        <div className="rental-wheel-pad" aria-hidden="true" />
      </div>
      <div className="rental-wheel-indicator" aria-hidden="true" />
    </div>
  )
}

function DateTimeField({
  label,
  value,
  onChange,
  allowPast,
  open,
  onOpen,
  onClose,
  quantity,
  occupancyByMonth,
  onVisibleMonth,
}) {
  const initialParts = wallParts(value || new Date())
  const [viewYear, setViewYear] = useState(initialParts.year)
  const [viewMonth, setViewMonth] = useState(initialParts.month)
  const [page, setPage] = useState('date')

  useEffect(() => {
    if (!open) return
    const parts = wallParts(value || new Date())
    setViewYear(parts.year)
    setViewMonth(parts.month)
    setPage('date')
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return
    onVisibleMonth?.(viewYear, viewMonth)
    if (value) {
      const parts = wallParts(value)
      if (parts.year !== viewYear || parts.month !== viewMonth) {
        onVisibleMonth?.(parts.year, parts.month)
      }
    }
  }, [open, viewYear, viewMonth, value, onVisibleMonth])

  const selectedParts = value ? wallParts(value) : null
  const baseParts = selectedParts || wallParts(new Date())
  const nowParts = wallParts(new Date())
  const isToday = (day) =>
    day.year === nowParts.year && day.month === nowParts.month && day.day === nowParts.day

  const firstWeekday = (() => {
    const day = new Date(Date.UTC(viewYear, viewMonth - 1, 1)).getUTCDay()
    return (day + 6) % 7 // Monday first
  })()
  const daysInMonth = new Date(Date.UTC(viewYear, viewMonth, 0)).getUTCDate()

  const cells = []
  for (let index = 0; index < firstWeekday; index += 1) cells.push(null)
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ year: viewYear, month: viewMonth, day })
  }

  function moveMonth(delta) {
    const next = new Date(Date.UTC(viewYear, viewMonth - 1 + delta, 1))
    setViewYear(next.getUTCFullYear())
    setViewMonth(next.getUTCMonth() + 1)
  }

  function applyParts(parts) {
    const next = fromWall(parts.year, parts.month, parts.day, parts.hour, parts.minute)
    if (!allowPast && next.getTime() < Date.now() - 60 * 1000) return
    onChange?.(next)
  }

  function selectDay(day) {
    applyParts({ ...day, hour: baseParts.hour, minute: baseParts.minute })
  }

  function selectHour(hour) {
    applyParts({ ...baseParts, hour })
  }

  function selectMinute(minute) {
    applyParts({ ...baseParts, minute })
  }

  const dateText = value ? formatRangeDate(value) : 'Выберите дату'
  const timeText = value ? formatRangeTime(value) : ''

  return (
    <div className={`rental-field${open ? ' rental-field--open' : ''}`}>
      <button
        type="button"
        className="rental-field-button"
        onClick={() => (open ? onClose?.() : onOpen?.())}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={timeText ? `${label}: ${dateText}, ${timeText}` : `${label}: ${dateText}`}
      >
        <span className="rental-field-label">{label}</span>
        <span className={value ? 'rental-field-date' : 'rental-field-date rental-field-date--empty'}>
          {dateText}
        </span>
        {timeText ? <span className="rental-field-time">{timeText}</span> : null}
      </button>

      {open ? createPortal(
        <div className="rental-cal-overlay" onMouseDown={onClose} role="presentation">
          <div
            className="rental-cal-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="rental-cal-top">
              <span className="rental-cal-title">{page === 'time' ? 'Время' : label}</span>
              <button type="button" className="rental-period-close" onClick={onClose} aria-label="Закрыть">
                ×
              </button>
            </div>
            {page === 'date' ? (
              <>
                <div className="rental-cal">
                  <div className="rental-cal-header">
                    <button type="button" onClick={() => moveMonth(-1)} aria-label="Предыдущий месяц">‹</button>
                    <strong>{MONTH_NAMES[viewMonth - 1]} {viewYear}</strong>
                    <button type="button" onClick={() => moveMonth(1)} aria-label="Следующий месяц">›</button>
                  </div>
                  <div className="rental-cal-weekdays">
                    {WEEKDAYS.map((dayName) => <span key={dayName}>{dayName}</span>)}
                  </div>
                  <div className="rental-cal-grid">
                    {cells.map((cell, index) => {
                      if (!cell) return <span key={`empty-${index}`} className="rental-cal-empty" />
                      const cellDate = fromWall(cell.year, cell.month, cell.day, 12, 0)
                      const selected = value && sameWallDay(cellDate, value)
                      const disabled = !allowPast
                        && (cell.year < nowParts.year
                          || (cell.year === nowParts.year && cell.month < nowParts.month)
                          || (cell.year === nowParts.year && cell.month === nowParts.month && cell.day < nowParts.day))
                      const state = bucketState(
                        readySteps(occupancyByMonth, cell.year, cell.month),
                        fromWall(cell.year, cell.month, cell.day, 0, 0),
                        fromWall(cell.year, cell.month, cell.day + 1, 0, 0),
                        quantity,
                        allowPast
                      )
                      return (
                        <button
                          key={`${cell.year}-${cell.month}-${cell.day}`}
                          type="button"
                          className={[
                            'rental-cal-day',
                            selected ? 'rental-cal-day--selected' : '',
                            !selected && isToday(cell) ? 'rental-cal-day--today' : '',
                            state ? `rental-cal-day--${state}` : '',
                          ].filter(Boolean).join(' ')}
                          disabled={disabled}
                          aria-label={state ? `${cell.day}, ${STATE_LABEL[state]}` : undefined}
                          onClick={() => selectDay(cell)}
                        >
                          <span className="rental-cal-day-num">{cell.day}</span>
                          <span className={state ? `rental-cal-day-dot rental-cal-day-dot--${state}` : 'rental-cal-day-dot'} />
                        </button>
                      )
                    })}
                  </div>
                </div>
                {readySteps(occupancyByMonth, viewYear, viewMonth) ? (
                  <ul className="rental-cal-legend">
                    <li><span className="rental-cal-swatch rental-cal-swatch--free" />Свободно</li>
                    <li><span className="rental-cal-swatch rental-cal-swatch--partial" />Частично</li>
                    <li><span className="rental-cal-swatch rental-cal-swatch--booked" />Занято</li>
                  </ul>
                ) : null}
                <button type="button" className="rental-cal-done" onClick={() => setPage('time')}>
                  Далее
                </button>
              </>
            ) : (
              <div className="rental-cal-time">
                <div className="rental-cal-time-columns">
                  <TimeWheel
                    values={HOURS}
                    selected={baseParts.hour}
                    onSelect={selectHour}
                    ariaLabel="Часы"
                    stateAt={(hour) => bucketState(
                      readySteps(occupancyByMonth, baseParts.year, baseParts.month),
                      fromWall(baseParts.year, baseParts.month, baseParts.day, hour, 0),
                      fromWall(baseParts.year, baseParts.month, baseParts.day, hour + 1, 0),
                      quantity,
                      allowPast
                    )}
                  />
                  <span className="rental-cal-time-arrow" aria-hidden="true">:</span>
                  <TimeWheel
                    values={MINUTES}
                    selected={baseParts.minute}
                    onSelect={selectMinute}
                    ariaLabel="Минуты"
                    stateAt={(minute) => {
                      const start = fromWall(baseParts.year, baseParts.month, baseParts.day, baseParts.hour, minute)
                      return bucketState(
                        readySteps(occupancyByMonth, baseParts.year, baseParts.month),
                        start,
                        new Date(start.getTime() + 5 * 60 * 1000),
                        quantity,
                        allowPast
                      )
                    }}
                  />
                </div>
                <div className="rental-cal-actions">
                  <button type="button" className="rental-cal-back" onClick={() => setPage('date')}>
                    Назад
                  </button>
                  <button type="button" className="rental-cal-done" onClick={() => onClose?.()}>
                    Готово
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      ) : null}
    </div>
  )
}

export default function RentalPeriodPicker({
  productName,
  unitPrice,
  durationMinutes,
  suggestions = [],
  allowPast = false,
  loadAvailable,
  loadOccupancy,
  onConfirm,
  onClose,
}) {
  const duration = Number(durationMinutes)
  const unitCopy = rentalUnitCopy(duration)
  const hasSuggestions = suggestions.length > 0
  const [step, setStep] = useState(hasSuggestions ? 'choose' : 'edit')
  const [startDate, setStartDate] = useState(() => nextFullHour())
  const [endDate, setEndDate] = useState(
    () => new Date(nextFullHour().getTime() + (Number(durationMinutes) || 60) * 60 * 1000)
  )
  const [quantity, setQuantity] = useState(1)
  const [openField, setOpenField] = useState(null)
  const [available, setAvailable] = useState(null)
  const [availabilityError, setAvailabilityError] = useState(null)
  const [occupancyByMonth, setOccupancyByMonth] = useState({})
  const occupancyRef = useRef({})
  const mountedRef = useRef(true)

  const start = startDate
  const end = endDate

  function changeStart(next) {
    if (!next) return
    if (start && end) {
      const delta = next.getTime() - start.getTime()
      setEndDate(new Date(end.getTime() + delta))
    }
    setStartDate(next)
  }

  const endIso = end ? end.toISOString() : ''
  const startIso = start ? start.toISOString() : ''
  const loadAvailableRef = useRef(loadAvailable)
  loadAvailableRef.current = loadAvailable
  const loadOccupancyRef = useRef(loadOccupancy)
  loadOccupancyRef.current = loadOccupancy

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const handleVisibleMonth = useCallback((year, month) => {
    const key = `${year}-${month}`
    const existing = occupancyRef.current[key]
    if (existing?.status === 'ready' || existing?.status === 'loading') return
    const load = loadOccupancyRef.current
    if (typeof load !== 'function') return
    occupancyRef.current[key] = { status: 'loading' }
    setOccupancyByMonth({ ...occupancyRef.current })
    const from = fromWall(year, month, 1, 0, 0)
    const to = fromWall(year, month + 1, 1, 0, 0)
    load(from.toISOString(), to.toISOString())
      .then((raw) => {
        if (!mountedRef.current) return
        occupancyRef.current[key] = { status: 'ready', steps: normalizeOccupancySteps(raw) }
        setOccupancyByMonth({ ...occupancyRef.current })
      })
      .catch(() => {
        if (!mountedRef.current) return
        delete occupancyRef.current[key]
        setOccupancyByMonth({ ...occupancyRef.current })
      })
  }, [])

  useEffect(() => {
    const load = loadAvailableRef.current
    if (!startIso || !endIso || endIso <= startIso || typeof load !== 'function') {
      setAvailable(null)
      return undefined
    }
    let cancelled = false
    setAvailabilityError(null)
    load(startIso, endIso)
      .then((count) => {
        if (cancelled) return
        const next = Number(count)
        setAvailable(Number.isFinite(next) ? next : 0)
      })
      .catch((error) => {
        if (cancelled) return
        setAvailable(null)
        setAvailabilityError(error?.message || 'Не удалось проверить наличие')
      })
    return () => {
      cancelled = true
    }
  }, [startIso, endIso])

  const pastStart = !allowPast && start && start.getTime() < Date.now() - 60 * 1000
  const rangeOk = Boolean(start && end && end > start && !pastStart)
  const factor = rangeOk ? rentalFactor(start, end, duration) : null
  const total = rangeOk ? rentalLineTotal(unitPrice, start, end, duration, quantity) : null
  const spanLabel = factor != null && unitCopy ? unitCopy.span(factor) : ''
  const tooMany = available != null && quantity > available
  const waitingForStock = typeof loadAvailable === 'function' && rangeOk && available == null && !availabilityError
  const canConfirm = rangeOk && !tooMany && !waitingForStock && quantity >= 1 && Number.isFinite(duration) && duration > 0

  function applySuggestion(suggestion) {
    setStartDate(new Date(suggestion.fromAt))
    setEndDate(new Date(suggestion.toAt))
    setStep('edit')
  }

  return createPortal(
    <div className="rental-period-overlay" onMouseDown={onClose} role="presentation">
      <div
        className="rental-period-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rental-period-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="rental-period-grip" aria-hidden="true" />
        <div className="rental-period-header">
          <div>
            <h2 id="rental-period-title" className="rental-period-title">{productName || 'Аренда'}</h2>
            <p className="rental-period-sub">
              {formatMoney(unitPrice)} сом{unitCopy ? ` ${unitCopy.rate}` : ''}
            </p>
          </div>
          <button type="button" className="rental-period-close" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        {step === 'choose' ? (
          <div className="rental-period-body">
            <p className="rental-period-lead">Выберите период, который уже есть в заказе, или новый.</p>
            <div className="rental-period-choices">
              {suggestions.map((suggestion) => {
                const from = new Date(suggestion.fromAt)
                const to = new Date(suggestion.toAt)
                const text = priceSentence({
                  base: unitPrice,
                  from,
                  to,
                  durationMinutes: duration,
                  quantity: 1,
                })
                return (
                  <button
                    key={`${suggestion.fromAt}|${suggestion.toAt}`}
                    type="button"
                    className="rental-period-choice"
                    onClick={() => applySuggestion(suggestion)}
                  >
                    <span className="rental-period-choice-name">Как у «{suggestion.productName}»</span>
                    <span className="rental-period-choice-text">{text || formatPeriodRange(from, to)}</span>
                  </button>
                )
              })}
              <button type="button" className="rental-period-choice rental-period-choice--new" onClick={() => setStep('edit')}>
                <span className="rental-period-choice-name">Другой период</span>
                <span className="rental-period-choice-text">Указать свои даты</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="rental-period-body">
            {hasSuggestions ? (
              <button type="button" className="rental-period-back" onClick={() => setStep('choose')}>
                ← К выбранным периодам
              </button>
            ) : null}

            <div className="rental-period-block">
              <span className="rental-period-section-label">Период аренды</span>
              <div className="rental-range">
                <DateTimeField
                  label="Забрать"
                  value={start}
                  onChange={changeStart}
                  allowPast={allowPast}
                  quantity={quantity}
                  occupancyByMonth={occupancyByMonth}
                  onVisibleMonth={handleVisibleMonth}
                  open={openField === 'start'}
                  onOpen={() => setOpenField('start')}
                  onClose={() => setOpenField(null)}
                />
                <span className="rental-range-arrow" aria-hidden="true">→</span>
                <DateTimeField
                  label="Вернуть"
                  value={end}
                  onChange={(next) => {
                    if (next) setEndDate(next)
                  }}
                  allowPast={allowPast}
                  quantity={quantity}
                  occupancyByMonth={occupancyByMonth}
                  onVisibleMonth={handleVisibleMonth}
                  open={openField === 'end'}
                  onOpen={() => setOpenField('end')}
                  onClose={() => setOpenField(null)}
                />
              </div>
              {pastStart ? <p className="rental-period-error">Начало не может быть в прошлом.</p> : null}
              {!pastStart && !rangeOk ? <p className="rental-period-error">Укажите возврат позже выдачи.</p> : null}
            </div>

            <div className="rental-qty">
              <div className="rental-qty-copy">
                <span className="rental-qty-label">Количество</span>
                {availabilityError ? (
                  <span className="rental-qty-stock rental-qty-stock--bad">{availabilityError}</span>
                ) : typeof loadAvailable === 'function' && rangeOk ? (
                  <span className={tooMany ? 'rental-qty-stock rental-qty-stock--bad' : 'rental-qty-stock'}>
                    {available == null ? 'Свободно: …' : `Свободно: ${available}`}
                  </span>
                ) : null}
              </div>
              <div className="rental-period-stepper-controls">
                <button
                  type="button"
                  onClick={() => setQuantity((count) => Math.max(1, count - 1))}
                  aria-label="Меньше штук"
                  disabled={quantity <= 1}
                >
                  −
                </button>
                <strong>{quantity}</strong>
                <button
                  type="button"
                  onClick={() => setQuantity((count) => count + 1)}
                  aria-label="Больше штук"
                  disabled={available != null && quantity >= available}
                >
                  +
                </button>
              </div>
            </div>

            <div className="rental-period-footer">
              <div className="rental-period-total" aria-live="polite">
                <span className="rental-period-total-kicker">Итого</span>
                <strong className="rental-period-total-sum">
                  {rangeOk && total != null ? `${formatMoney(total)} сом` : '—'}
                </strong>
                {rangeOk && spanLabel ? (
                  <span className="rental-period-total-meta">
                    {quantity} шт · {spanLabel}
                    <span className="rental-period-total-rate">
                      {` · ${formatMoney(unitPrice)} сом/${unitCopy.per}`}
                    </span>
                  </span>
                ) : (
                  <span className="rental-period-total-meta">{quantity} шт</span>
                )}
              </div>
              <button
                type="button"
                className="rental-period-confirm"
                disabled={!canConfirm}
                onClick={() => {
                  if (!canConfirm) return
                  onConfirm({
                    fromAt: start.toISOString(),
                    toAt: end.toISOString(),
                    quantity,
                  })
                }}
              >
                Добавить
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
